import {
  Avatar,
  Badge,
  Box,
  Card,
  CardBody,
  Divider,
  Flex,
  Grid,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  Package,
  Calendar,
  AlertCircle,
  TrendingUp,
  Hash,
  MoreVertical,
} from "lucide-react";
import { getStatusColor, getStatusColorAndBg } from "@/util/getStatusColor";
import { Product } from "@/lib/services/productService";

interface Permissions {
  can_edit: boolean;
  can_delete: boolean;
  can_create: boolean;
  can_view: boolean;
}

interface MobileProductCardProps {
  product: Product;
  permissions: Permissions;
  //onViewDetails: () => void;
  //onEdit: () => void;
  //onDelete: () => void;
  onReplenish: () => void;
}

const MobileProductCard = ({
  product,
  permissions,
  onReplenish,
}: MobileProductCardProps) => {
  const getQuantityColor = (quantity: number, threshold: number) => {
    if (quantity === 0) return "red";
    if (quantity <= threshold) return "orange";
    return "green";
  };

  const formatExpirationDate = (date: string) => {
    const expDate = new Date(date);
    const today = new Date();
    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return (
        <Text as="span" color="red.600" fontWeight="medium">
          Expired ({Math.abs(diffDays)} days ago)
        </Text>
      );
    } else if (diffDays <= 30) {
      return (
        <Text as="span" color="orange.600" fontWeight="medium">
          Expires in {diffDays} days
        </Text>
      );
    } else {
      return date;
    }
  };

  return (
    <Card
      mb={3}
      shadow="sm"
      _hover={{ shadow: "md" }}
      transition="all 0.2s"
      borderWidth="1px"
      borderColor="gray.200"
    >
      <CardBody p={4}>
        <Flex justify="space-between" align="start" mb={3}>
          <HStack spacing={2} flex={1}>
            <Package size={14} color="#718096" />
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {product.serial_no}
            </Text>
          </HStack>
          <HStack spacing={1}>
            <Badge
              colorScheme={getQuantityColor(
                product.quantity,
                product.threshold,
              )}
              fontSize="xs"
              px={2}
              py={1}
              borderRadius="md"
            >
              {product.quantity} units
            </Badge>
            {(permissions.can_edit || permissions.can_delete) && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<MoreVertical size={16} />}
                  variant="ghost"
                  size="sm"
                  aria-label="Options"
                />
                <MenuList fontSize="sm">
                  {permissions.can_edit && (
                    <MenuItem onClick={onReplenish}>Replenish Stock</MenuItem>
                  )}
                </MenuList>
              </Menu>
            )}
          </HStack>
        </Flex>

        <Box mb={4}>
          <Text fontSize="md" fontWeight="bold" mb={1}>
            {product.product_name}
          </Text>
          <Text fontSize="xs" color="gray.600" mb={2}>
            Category: {product.category}
          </Text>
          <Badge
            colorScheme={getStatusColor(product.status)}
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
            variant="outline"
          >
            {product.status}
          </Badge>
        </Box>

        <VStack spacing={3} align="stretch">
          {/* Product Numbers */}
          <Box>
            <HStack spacing={2} mb={1.5}>
              <Hash size={12} color="#9CA3AF" />
              <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                PRODUCT NUMBERS
              </Text>
            </HStack>
            <GridInfo label="Lot No." value={product.lot_no} />
            <GridInfo label="Batch No." value={`B${product.batch_no}`} />
          </Box>

          <Divider />

          {/* Stock & Expiry */}
          <Box>
            <HStack spacing={2} mb={1.5}>
              <AlertCircle size={12} color="#9CA3AF" />
              <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                STOCK INFORMATION
              </Text>
            </HStack>
            <GridInfo label="Quantity" value={`${product.quantity} units`} />
            <GridInfo label="Threshold" value={`${product.threshold} units`} />
            <GridInfo
              label="Expiration"
              value={formatExpirationDate(product.expiration_date)}
            />
          </Box>

          <Divider />

          {/* Product Type */}
          {product.type && (
            <>
              <Box>
                <HStack spacing={2} mb={1.5}>
                  <Package size={12} color="#9CA3AF" />
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                    PRODUCT TYPE
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="medium">
                  {product.type}
                </Text>
              </Box>
              <Divider />
            </>
          )}

          {/* Status Indicators */}
          <Box>
            <HStack spacing={2} mb={1.5}>
              <Calendar size={12} color="#9CA3AF" />
              <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                STATUS INDICATORS
              </Text>
            </HStack>
            <Grid templateColumns="repeat(2, 1fr)" gap={2}>
              <StatusIndicator
                label="Stock Level"
                color={getQuantityColor(product.quantity, product.threshold)}
                value={
                  product.quantity === 0
                    ? "Critical"
                    : product.quantity <= product.threshold
                      ? "Low"
                      : "Good"
                }
              />
              <StatusIndicator
                label="Expiry Status"
                color={getStatusColorAndBg(product.status).color}
                value={product.status}
              />
            </Grid>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
};

// Helper component for grid layout info
const GridInfo = ({
  label,
  value,
}: {
  label: string;
  value: string | React.ReactNode;
}) => (
  <Flex justify="space-between" align="center" mb={1}>
    <Text fontSize="xs" color="gray.600">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {value}
    </Text>
  </Flex>
);

// Helper component for status indicators
const StatusIndicator = ({
  label,
  color,

  value,
}: {
  label: string;
  color: string;
  value: string;
}) => (
  <Box>
    <Text fontSize="xs" color="gray.600" mb={1}>
      {label}
    </Text>
    <Badge colorScheme={color} fontSize="xs" px={2} py={0.5} borderRadius="md">
      {value}
    </Badge>
  </Box>
);

export default MobileProductCard;
